// The Profile sheet (spec item 8): your interests (starred types), hidden types and events, the other types in use,
// reset to the starting list, and where the data stands. Port of web/profile-ui.js profilePanel; same copy.
import { useState } from "react";
import { SHARED, T } from "../../shared/config";
import { Icon } from "../../shared/icons";
import { RULES } from "../../shared/rules";
import { normTag } from "../../profile/model";
import { emojiOf, eventById, tagEmoji, useFeed } from "../../state/feed";
import { getProfile, useProfileEvents, useProfileSync, useProfileView } from "../../state/profile";
import { closeSheet } from "../../state/view";
import styles from "./profile.module.css";

const withEmoji = (t: string) => (tagEmoji(t) ? `${tagEmoji(t)} ${t}` : t);
const SOURCE_TEXT: Record<string, string> = { server: "Live from server", cached: "Offline (saved events)", error: "Offline", loading: "Loading" };

export function ProfilePanel() {
  const v = useProfileView();
  useProfileEvents();
  const { loaded, pending, rejected } = useProfileSync();
  const tags = useFeed((s) => s.tags);
  const source = useFeed((s) => s.source);
  useFeed((s) => s.tagEmoji);
  const profile = getProfile();
  const [draft, setDraft] = useState("");
  const interests = [...v.interests];
  const others = tags.map((t) => t.name.toLowerCase()).filter((t) => !v.interests.has(t));
  const hiddenEvents = [...v.hiddenEvents].reverse().map((id) => eventById(id) ?? { id, title: "Hidden event", start: null, tags: [] });
  const add = () => {
    const t = normTag(draft);
    if (t && !v.interests.has(t)) profile.setTagLevel(t, "interest");
    setDraft("");
  };
  return (
    <div id="profile" className={styles["profile"]}>
      <div className={styles["list-head"]}>
        <h3>{T("profile")}</h3>
        <button type="button" className={styles["close"]} aria-label={T("close")} onClick={closeSheet}><Icon name="x" /></button>
      </div>
      <section>
        <div className={styles["label"]}>{T("profile.interests", { n: interests.length })}</div>
        {/* One list of interests, each with its ★; tapping the ★ takes the interest off. */}
        {interests.map((t) => (
          <div key={t} className={styles["interest-row"]} data-interest={t}>
            <button type="button" className={styles["star"]} aria-pressed="true" aria-label={`Remove ${t}`} onClick={() => profile.setTagLevel(t, null)}>
              <Icon name="star-filled" />
            </button>
            <span className={styles["interest-name"]}>{withEmoji(t)}</span>
          </div>
        ))}
        <div className={styles["interest-add"]}>
          <input type="text" id="interest-new" maxLength={200} placeholder={T("profile.addInterest")} autoComplete="off" value={draft}
            onChange={(ev) => setDraft(ev.target.value)} onKeyDown={(ev) => { if (ev.key === "Enter") add(); }} />
          <button type="button" className={styles["btn"]} onClick={add}>{T("profile.add")}</button>
        </div>
        <div className={styles["hint"]}>{T("profile.interestsHint")}</div>
      </section>
      {(v.hiddenTags.size > 0 || hiddenEvents.length > 0) && (
        <section id="hidden-section">
          <div className={styles["label"]}>{T("profile.hidden")}</div>
          {v.hiddenTags.size > 0 && (
            <div className={styles["tags"]}>
              {[...v.hiddenTags].map((t) => (
                <button key={t} type="button" className={styles["chip"]} data-hidden-tag={t} aria-label={`Unhide ${t}`} onClick={() => profile.setTagLevel(t, null)}>
                  {withEmoji(t)}<Icon name="x" />
                </button>
              ))}
            </div>
          )}
          {hiddenEvents.map((e) => (
            <button key={e.id} type="button" className={styles["hidden-row"]} data-hidden-event={e.id} onClick={() => profile.unhideEvent(e.id)}>
              <span aria-hidden="true">{e.start ? emojiOf(e as never) : SHARED.fallbackEmoji}</span>
              <span className={styles["result-text"]}>
                <div className={styles["t"]}>{e.title}</div>
                {e.start && <div className={styles["meta"]}>{`${RULES.dayName(e.start, new Date())} ${RULES.timeString(e.start)}`}</div>}
              </span>
              <span className={styles["unhide"]}>{T("profile.unhide")}</span>
            </button>
          ))}
          <div className={styles["hint"]}>{T("profile.hiddenHint")}</div>
        </section>
      )}
      {others.length > 0 && (
        <section>
          <div className={styles["label"]}>{T("profile.otherTags")}</div>
          <div className={styles["tags"]}>
            {others.map((t) => (
              <button key={t} type="button" className={styles["chip"]} onClick={() => profile.setTagLevel(t, "interest")}><Icon name="star" />{withEmoji(t)}</button>
            ))}
          </div>
          <div className={styles["hint"]}>{T("profile.otherTagsHint")}</div>
        </section>
      )}
      <section>
        <button type="button" className={`${styles["btn"]} ${styles["danger"]}`} onClick={() => { if (confirm(T("profile.resetConfirm"))) profile.resetInterests(); }}>
          {T("profile.reset")}
        </button>
      </section>
      <section>
        <div className={styles["label"]}>About</div>
        <div className={`${styles["muted"]} ${styles["small"]}`}>{`Server: ${location.origin}`}</div>
        <div className={`${styles["muted"]} ${styles["small"]}`}>{`Data: ${SOURCE_TEXT[source] ?? source}`}</div>
        <div className={`${styles["muted"]} ${styles["small"]}`}>
          {pending ? "Profile: saving… (will retry when the server is back)" : loaded ? "Profile: synced with your phone" : "Profile: connecting…"}
        </div>
        {rejected > 0 && (
          <div id="profile-rejected" className={`${styles["small"]} ${styles["warn"]}`} role="status">
            {`${rejected === 1 ? "1 change" : `${rejected} changes`} couldn't be saved: the server refused ${rejected === 1 ? "it" : "them"}, so ${rejected === 1 ? "it was" : "they were"} undone.`}
          </div>
        )}
      </section>
    </div>
  );
}
